import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowClockwise, Brain, CaretRight, CheckCircle, Database, Microphone, Paperclip,
  PaperPlaneTilt, Robot, ShieldCheck, Sparkle, WarningCircle, Waveform,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import MasterDetailPane from "../components/MasterDetailPane";
import { cn } from "../lib/utils";
import { latestRequest } from "../lib/latestRequest";
import { pageIsVisible, usePageVisibility } from "../lib/usePageVisibility";
import { useNativeSupportBootstrap } from "../lib/nativeSupportBootstrap";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Slider } from "../components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import ExtractionReview from "../components/ExtractionReview";
import OperationsActionResult from "../components/OperationsActionResult";
import StructuredDataTable, {
  humanizeFieldName,
  parseStructuredValue,
} from "../components/StructuredDataTable";
import {
  applyOperationsRunRule,
  assignAttentionItem,
  executeOperationsCommand,
  getApiErrorMessage,
  getAttentionItem,
  openOperationsSocket,
  getOperationsRunStory,
  getOperationsToday,
  getOperationsTodayQueue,
  getOperationsTodayOutcomes,
  issueAttentionConfirmation,
  performAttentionAction,
  previewOperationsCommand,
  previewOperationsRunRule,
  queryOperationsAssistant,
  uploadOperationsDocument,
  updateAttentionItem,
  createOrchestratorTriggerAsync,
} from "../lib/api";


const severityClasses = {
  critical: "border-destructive/30 bg-destructive/5 text-destructive",
  high: "border-warning/30 bg-warning/5 text-warning",
  medium: "border-info/30 bg-info/5 text-info",
  low: "border-border bg-muted/30 text-muted-foreground",
};

const relativeTime = (value) => {
  if (!value) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const entityLabel = (entityRef) => (
  entityRef?.label || entityRef?.name || entityRef?.external_id || entityRef?.id || null
);

const sentenceLabel = (value) => {
  const label = humanizeFieldName(value);
  return label ? `${label.charAt(0).toUpperCase()}${label.slice(1)}` : "";
};

const friendlyInlineCopy = (value) => String(value || "")
  .replace(/\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/g, (match) => match.replaceAll("_", " "));

const formatOutcomeValue = (key, value) => {
  if ((key === "file_size" || key === "sampled_bytes") && Number.isFinite(Number(value))) {
    const bytes = Number(value);
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} bytes`;
  }
  if (key === "confidence" && Number.isFinite(Number(value))) {
    return `${Math.round(Number(value) * 100)}%`;
  }
  if (typeof value === "string" && ["status", "disposition", "document_type", "category", "action_disposition"].includes(key)) {
    return sentenceLabel(value);
  }
  if (Array.isArray(value)) return value.map((item) => formatOutcomeDetails(item));
  if (value && typeof value === "object") return formatOutcomeDetails(value);
  return value;
};

const formatOutcomeDetails = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !["outcome", "summary", "message", "description", "result_summary"].includes(key))
      .map(([key, child]) => [key, formatOutcomeValue(key, child)]),
  );
};

const presentOutcome = (value) => {
  const parsed = parseStructuredValue(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {
      structured: false,
      summary: typeof parsed === "string" && parsed.trim() ? parsed : "The run reached its intended outcome.",
      typeLabel: "",
      details: null,
    };
  }
  const summary = [parsed.summary, parsed.result_summary, parsed.message, parsed.description]
    .find((candidate) => typeof candidate === "string" && candidate.trim());
  const type = parsed.outcome || parsed.result || parsed.status || parsed.disposition;
  return {
    structured: true,
    summary: summary || (type ? `${sentenceLabel(type)}.` : "The run reached its intended outcome."),
    typeLabel: type ? sentenceLabel(type) : "",
    details: formatOutcomeDetails(parsed),
  };
};

const outcomeTitle = (outcome, presentation) => {
  const title = outcome?.title?.trim();
  if ((!title || title.toLowerCase() === "completed operation") && presentation.typeLabel) {
    return presentation.typeLabel;
  }
  return title || presentation.typeLabel || "Completed operation";
};

/** A count in the Today toolbar. Replaces CalmStat's card, which spent most of its height on padding. */
function TodayChip({ value, label, detail, tone = "neutral", actionLabel, onClick, expanded }) {
  const chip = (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] transition-colors",
        tone === "attention"
          ? "border-warning-border bg-warning-surface text-warning-foreground"
          : "border-border bg-muted/40 text-muted-foreground",
        onClick && "hover:bg-accent/60",
      )}
    >
      <b className="font-semibold tabular-nums text-foreground">{value}</b>
      {label}
      {actionLabel && (
        <span className="inline-flex items-center gap-0.5 font-semibold text-primary">
          {actionLabel}<CaretRight size={9} className={expanded ? "rotate-90" : undefined} />
        </span>
      )}
    </span>
  );
  if (!onClick) return <span title={detail}>{chip}</span>;
  return (
    <button type="button" onClick={onClick} title={detail} aria-expanded={expanded} className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {chip}
    </button>
  );
}

function CalmStat({ label, value, detail, actionLabel, onClick, expanded }) {
  const content = (
    <div className="flex min-w-0 items-center gap-2">
      <p className="min-w-[42px] text-base font-semibold leading-none tracking-[-0.035em] text-foreground">{value}</p>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</p>
        <div className="flex min-w-0 items-center gap-1.5">
          {detail && <p className="truncate text-[11px] leading-4 text-muted-foreground">{detail}</p>}
          {actionLabel && (
            <p className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-semibold text-primary">
              {actionLabel}<CaretRight size={10} />
            </p>
          )}
        </div>
      </div>
    </div>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-expanded={expanded}
        className="min-w-0 px-2 py-1.5 text-left transition-colors hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        {content}
      </button>
    );
  }
  return (
    <div className="min-w-0 px-2 py-1.5">
      {content}
    </div>
  );
}

function QuickConfirmationAction({ item, action, label, onComplete, variant = "default" }) {
  const [intent, setIntent] = useState(null);
  const [value, setValue] = useState([0]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const prepare = async (event) => {
    event.stopPropagation();
    setOpen(true);
    setBusy(true);
    try {
      const response = await issueAttentionConfirmation(item.id, {
        action,
        parameters: item.proposed_action || {},
        idempotency_key: `${item.id}:${action}:${Date.now()}`,
      });
      setIntent(response.data);
      setValue([0]);
    } catch (error) {
      setOpen(false);
      toast.error(getApiErrorMessage(error, "Could not prepare confirmation"));
    } finally {
      setBusy(false);
    }
  };

  const commit = async (next) => {
    if (next[0] < 95 || !intent || busy) {
      setValue(next[0] < 95 ? [0] : next);
      return;
    }
    setBusy(true);
    try {
      await performAttentionAction(item.id, { action, confirmation_token: intent.token });
      toast.success(action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Monitoring resumed");
      setOpen(false);
      setIntent(null);
      onComplete();
    } catch (error) {
      setOpen(false);
      setIntent(null);
      toast.error(getApiErrorMessage(error, "Action could not be completed"));
    } finally {
      setBusy(false);
      setValue([0]);
    }
  };

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) { setIntent(null); setValue([0]); } }}>
      <PopoverTrigger asChild>
        <Button type="button" variant={variant} size="xs" onClick={prepare} disabled={busy}>{label}</Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3" onClick={(event) => event.stopPropagation()}>
        <p className="text-xs font-semibold text-foreground">Confirm {label.toLowerCase()}</p>
        <p className="mt-1 text-[11px] leading-4 text-muted-foreground">This remains governed: authority and policy are checked again when you finish the slide.</p>
        {intent ? <div className="mt-3 flex items-center gap-3"><Slider value={value} onValueChange={setValue} onValueCommit={commit} max={100} step={1} disabled={busy} aria-label={`Slide to ${action}`} /><span className="w-8 text-right text-[11px] font-semibold text-primary">{value[0]}%</span></div> : <Skeleton className="mt-3 h-5 w-full" />}
      </PopoverContent>
    </Popover>
  );
}

// A website task waiting for a person links to its run, where the question is answered;
// one that stopped needing a person links to Portal Desk V2 with the task filled in.
const answerPath = (item) => (item.kind === "input_required" && item.state !== "resolved" &&
  String(item.metadata?.open_path || "").startsWith("/portal-desk-v2?") ? item.metadata.open_path : "");
const rerun = (item) => item.metadata?.request_kind === "needs_you";

function AttentionRow({ item, active, onSelect, onChanged, showQuickActions = true }) {
  const navigate = useNavigate();
  const entity = entityLabel(item.entity_ref);
  // A stopped website task stays until someone runs it again or says it is dealt with.
  const markDone = async (event) => {
    event.stopPropagation();
    try {
      await updateAttentionItem(item.id, { state: "resolved" });
      toast.success("Marked done");
      onChanged();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not mark this done"));
    }
  };
  const snooze = async (event) => {
    event.stopPropagation();
    try {
      await updateAttentionItem(item.id, {
        state: "snoozed",
        snoozed_until: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
      });
      toast.success("Snoozed for one hour");
      onChanged();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not snooze this item"));
    }
  };
  return (
    <div
      data-testid="operations-attention-row"
      className={`relative flex h-9 w-full items-center overflow-hidden rounded-md border pl-2 text-left transition-colors ${
        active ? "border-border bg-accent/45 shadow-sm" : "border-transparent hover:border-border hover:bg-accent/25"
      }`}
    >
      <button type="button" onClick={onSelect} aria-expanded={active} className="flex h-full min-w-0 flex-1 items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${
          item.severity === "critical" ? "bg-destructive" : item.severity === "high" ? "bg-warning" : "bg-info"
        }`} />
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-foreground">{friendlyInlineCopy(item.title)}</span>
        <span className="hidden max-w-[220px] shrink-0 truncate text-[11px] text-muted-foreground md:inline">{item.job_function}{entity ? ` · ${entity}` : ""}</span>
        {item.metadata?.occurrences > 1 && <span className="hidden shrink-0 rounded-full bg-muted px-1.5 text-[11px] font-medium text-muted-foreground xl:inline">{item.metadata.occurrences} grouped</span>}
        <span className="w-14 shrink-0 text-right text-[11px] text-muted-foreground">{relativeTime(item.created_at)}</span>
      </button>
      {answerPath(item) && <Button type="button" size="xs" className="shrink-0" onClick={() => navigate(answerPath(item))}>{rerun(item) ? "Run again" : "Answer"}</Button>}
      {rerun(item) && item.state !== "resolved" && item.can_act && <Button type="button" size="xs" variant="outline" className="ml-1 shrink-0" onClick={markDone}>Done</Button>}
      {item.can_act && showQuickActions && <div className="flex shrink-0 items-center gap-0.5 px-1">
        {item.source_type === "approval" && <>
          <QuickConfirmationAction item={item} action="approve" label="Approve" onComplete={onChanged} />
          <QuickConfirmationAction item={item} action="reject" label="Reject" variant="outline" onComplete={onChanged} />
        </>}
        {item.source_type === "signal_health" && <QuickConfirmationAction item={item} action="resume_binding" label="Resume" onComplete={onChanged} />}
        <Button type="button" variant="ghost" size="xs" className="hidden sm:inline-flex" onClick={snooze}>Snooze</Button>
      </div>}
      <Button type="button" variant="ghost" size="xs" className="mr-0.5 shrink-0" onClick={onSelect}>{active ? "Close" : "Details"}</Button>
    </div>
  );
}

function OperationalStatusBar({ mode, health, setup, onChangeMode, onReviewSetup }) {
  const setupCount = setup.visible ? Number(setup.counts?.active || 0) : 0;
  const coverageTone = !health?.configured || !health?.healthy || setupCount
    ? "border-warning/30 bg-warning/5"
    : "border-success/25 bg-success/5";
  const status = !health?.configured
    ? "Operational signals are not configured"
    : !health?.healthy
      ? `${health.unhealthy_count || 0} signal source${health.unhealthy_count === 1 ? "" : "s"} need review`
      : setupCount
        ? `${setupCount} automation setup gap${setupCount === 1 ? "" : "s"}`
        : "Coverage healthy";
  const modeLabel = String(mode.mode || "live").replaceAll("_", " ");

  return (
    <section className={`flex min-h-8 items-center justify-between gap-1 overflow-hidden rounded-md border-l-2 px-2 py-1 ${coverageTone}`}>
      <div className="flex min-w-0 items-center gap-1.5">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${health?.configured && health?.healthy && !setupCount ? "bg-success" : "bg-warning"}`} />
        <div className="flex min-w-0 items-center gap-1.5">
          <p className="whitespace-nowrap text-[11px] font-semibold capitalize text-foreground">{modeLabel} mode</p>
          <span className="text-muted-foreground">·</span>
          <p className="truncate text-[11px] text-muted-foreground">{status}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {setupCount > 0 && <Button variant="ghost" size="xs" onClick={onReviewSetup}>Review setup</Button>}
        <Button variant="ghost" size="xs" onClick={onChangeMode}>Change mode</Button>
      </div>
    </section>
  );
}

function ConfirmationControl({ item, action, label, onComplete }) {
  const [intent, setIntent] = useState(null);
  const [value, setValue] = useState([0]);
  const [busy, setBusy] = useState(false);

  const prepare = async () => {
    setBusy(true);
    try {
      const response = await issueAttentionConfirmation(item.id, {
        action,
        parameters: item.proposed_action || {},
        idempotency_key: `${item.id}:${action}:${Date.now()}`,
      });
      setIntent(response.data);
      setValue([0]);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not prepare confirmation"));
    } finally {
      setBusy(false);
    }
  };

  const commit = async (next) => {
    if (next[0] < 95 || !intent || busy) {
      setValue(next[0] < 95 ? [0] : next);
      return;
    }
    setBusy(true);
    try {
      await performAttentionAction(item.id, {
        action,
        confirmation_token: intent.token,
      });
      toast.success(action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Monitoring resumed");
      setIntent(null);
      setValue([0]);
      onComplete();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Action could not be completed"));
      setIntent(null);
      setValue([0]);
    } finally {
      setBusy(false);
    }
  };

  if (!intent) {
    return (
      <Button variant={action === "reject" ? "outline" : "default"} onClick={prepare} disabled={busy}>
        {busy ? "Preparing…" : label}
      </Button>
    );
  }

  return (
    <div className="w-full rounded-xl border border-primary/30 bg-primary/5 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Slide to {action.replace("_", " ")}</p>
          <p className="text-[11px] text-muted-foreground">Authority and policy will be checked again.</p>
        </div>
        <span className="text-xs font-semibold text-primary">{value[0]}%</span>
      </div>
      <Slider
        value={value}
        onValueChange={setValue}
        onValueCommit={commit}
        max={100}
        step={1}
        disabled={busy}
        aria-label={`Slide to ${action}`}
      />
    </div>
  );
}

function CommandControl({ preview, onCancel, onComplete }) {
  const [value, setValue] = useState([0]);
  const [busy, setBusy] = useState(false);
  if (!preview) return null;
  const commit = async (next) => {
    if (next[0] < 95 || busy) { setValue(next[0] < 95 ? [0] : next); return; }
    setBusy(true);
    try {
      const response = await executeOperationsCommand({ command_kind: preview.command_kind, confirmation_token: preview.token });
      toast.success("Command completed"); onComplete(response.data);
    } catch (error) { toast.error(getApiErrorMessage(error, "Command could not be completed")); onCancel(); }
    finally { setBusy(false); }
  };
  return <Card className="border-primary/30 bg-primary/5"><CardContent className="p-4"><p className="text-sm font-semibold text-foreground">{preview.summary}</p><p className="mt-1 text-xs text-muted-foreground">This short-lived preview will re-check authority and safety mode.</p>{preview.impact && Object.keys(preview.impact).length > 0 && <div className="mt-3"><StructuredDataTable data={preview.impact} /></div>}<div className="mt-3 flex items-center gap-3"><Slider value={value} onValueChange={setValue} onValueCommit={commit} max={100} step={1} disabled={busy} aria-label="Slide to confirm command" /><span className="text-xs font-semibold text-primary">{value[0]}%</span></div><Button className="mt-2" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button></CardContent></Card>;
}

function RunStory({ item }) {
  const story = item?.run_story;
  if (!story) return null;
  const root = story.executions?.[0] || {};
  return (
    <div className="space-y-5">
      <section>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">What triggered this</p>
        <p className="mt-2 text-sm leading-6 text-foreground">
          {root.trigger?.context?.summary || root.trigger?.context?.subject || root.trigger?.trigger_type || "An operational fact entered the governed router."}
        </p>
      </section>

      {root.skill_id && <section className="rounded-xl border border-info/30 bg-info/5 p-4"><div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 shrink-0 text-info" size={20} /><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><p className="text-sm font-semibold text-info">Selected Skill</p>{root.skill_version && <Badge variant="outline">v{root.skill_version}</Badge>}</div><p className="mt-2 break-all font-mono text-sm text-foreground">{root.skill_id}</p></div></div></section>}

      {root.orchestrator_plan && <section className="rounded-xl border border-primary/40 bg-primary/5 p-4"><div className="flex items-start gap-3"><Brain className="mt-0.5 shrink-0 text-primary" size={20} /><div><p className="text-sm font-semibold text-primary">Orchestrator reasoning</p><p className="mt-2 text-sm leading-6 text-muted-foreground">{root.orchestrator_plan}</p></div></div></section>}

      {story.facts?.length > 0 && <section><p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Facts and provenance</p><div className="mt-2 grid gap-2 sm:grid-cols-2">{story.facts.slice(0, 8).map((fact, index) => <div key={fact.id || index} className="rounded-lg border border-border bg-muted/30 p-3"><p className="text-xs font-medium text-foreground">{fact.name || fact.key || fact.fact_type || "Verified fact"}</p><p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{String(fact.value ?? fact.summary ?? "Recorded")}</p><p className="mt-1 text-[11px] text-muted-foreground">{fact.source || fact.provenance?.source || "Compute plane"}</p></div>)}</div></section>}

      {story.directives?.length > 0 && <section><p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rules applied</p><div className="mt-2 space-y-2">{story.directives.map((rule) => <div key={rule.id} className="flex gap-2 rounded-lg bg-muted/40 p-3 text-xs"><Badge variant="outline" className={rule.kind === "hard" ? "border-destructive/30 text-destructive" : "border-info/30 text-info"}>{rule.kind}</Badge><span className="text-muted-foreground">{rule.text}</span></div>)}</div></section>}

      <section>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">What ActionWeave did</p>
        <div className="mt-2 space-y-2">
          {(story.actions || []).slice(0, 6).map((action) => (
            <div key={action.id} className="flex items-start gap-2 rounded-lg bg-muted/40 px-3 py-2">
              {action.verification_status === "needs_review" ? <WarningCircle className="mt-0.5 text-warning" size={16} /> : action.success ? <CheckCircle className="mt-0.5 text-success" size={16} /> : <WarningCircle className="mt-0.5 text-destructive" size={16} />}
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground">{action.action}</p>
                <OperationsActionResult value={action.display_summary || action.error} />
              </div>
            </div>
          ))}
          {!story.actions?.length && <p className="text-xs text-muted-foreground">No external effects were attempted.</p>}
        </div>
      </section>

      {story.communications?.length > 0 && <section><p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Communications</p><div className="mt-2 space-y-2">{story.communications.slice(0, 6).map((message, index) => <div key={message.id || index} className="rounded-lg border border-border p-3 text-xs text-muted-foreground"><span className="font-medium text-foreground">{message.channel || message.action || "Message"}</span><OperationsActionResult value={message.result_summary || message.status} fallback="Recorded" /></div>)}</div></section>}

      <section className="grid gap-3 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-3"><div><p className="text-[11px] uppercase text-muted-foreground">Policy</p><p className="mt-1 text-xs font-medium text-foreground">{story.policy?.mode || "governed"}</p></div><div><p className="text-[11px] uppercase text-muted-foreground">Verified effects</p><p className="mt-1 text-xs font-medium text-foreground">{story.verified_effects?.length || 0}</p></div><div><p className="text-[11px] uppercase text-muted-foreground">Recorded cost</p><p className="mt-1 text-xs font-medium text-foreground">${Number(story.cost?.estimated_usd || root.estimated_cost_usd || 0).toFixed(2)}</p></div></section>

      <details className="rounded-lg border border-border bg-card px-3 py-2">
        <summary className="cursor-pointer text-xs font-medium text-foreground">Full execution trace</summary>
        <div className="mt-3 space-y-2">
          {(story.executions || []).map((execution) => (
            <div key={execution.id} className="flex items-center justify-between gap-3 text-[11px]">
              <span className="truncate text-muted-foreground">{execution.skill_id || "Router"}</span>
              <Badge variant="outline">{execution.status}</Badge>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

function OutcomesPanel({ outcomes, selectedId, onSelect, story, loadingStory, onClose }) {
  const selected = outcomes.items.find((item) => item.id === selectedId) || null;
  const selectedPresentation = selected ? presentOutcome(selected.final_outcome) : null;
  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex items-start justify-between gap-4 border-b border-border p-5">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Completed outcomes</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {outcomes.total} completed root run{outcomes.total === 1 ? "" : "s"} in the last {outcomes.window_hours || 24} hours
              {outcomes.total > outcomes.items.length ? ` · showing ${outcomes.items.length}` : ""}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>Hide details</Button>
        </div>

        {!outcomes.items.length ? (
          <p className="p-8 text-center text-sm text-muted-foreground">No completed outcomes are visible in your operational scope.</p>
        ) : (
          <div className="grid lg:grid-cols-[minmax(280px,0.72fr)_minmax(440px,1.28fr)]">
            <div className="divide-y divide-border border-b border-border lg:border-b-0 lg:border-r">
              {outcomes.items.map((outcome) => {
                const presentation = presentOutcome(outcome.final_outcome);
                return (
                <button
                  key={outcome.id}
                  type="button"
                  onClick={() => onSelect(outcome.id)}
                  aria-current={outcome.id === selectedId ? "true" : undefined}
                  className={`w-full p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                    outcome.id === selectedId ? "bg-primary/5" : "hover:bg-accent/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-semibold leading-5 text-foreground">{outcomeTitle(outcome, presentation)}</p>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{relativeTime(outcome.completed_at || outcome.updated_at)}</span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
                    {presentation.summary}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{outcome.job_function}</Badge>
                    <span className="text-[11px] text-muted-foreground">{outcome.autonomous ? "Autonomous" : "Human reviewed"}</span>
                  </div>
                </button>
                );
              })}
            </div>

            <div className="p-5 md:p-6">
              {selected && (
                <div className="mb-5 overflow-hidden rounded-2xl border border-success/25 bg-gradient-to-br from-success/10 via-card to-card p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border-success/30 bg-success/5 text-success">Completed</Badge>
                    <span className="text-xs text-muted-foreground">{selected.job_function}</span>
                  </div>
                  <h3 className="mt-3 text-xl font-semibold tracking-tight text-foreground">{outcomeTitle(selected, selectedPresentation)}</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {selectedPresentation.summary}
                  </p>
                </div>
              )}
              {selectedPresentation?.structured && selectedPresentation.details && Object.keys(selectedPresentation.details).length > 0 && (
                <section className="mb-5 rounded-xl border border-border bg-muted/20 p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <Database className="text-primary" size={18} />
                    <h4 className="text-sm font-semibold text-foreground">Outcome details</h4>
                  </div>
                  <StructuredDataTable data={selectedPresentation.details} />
                </section>
              )}
              {loadingStory && <div className="space-y-3"><Skeleton className="h-16" /><Skeleton className="h-24" /><Skeleton className="h-10" /></div>}
              {!loadingStory && story && <RunStory item={{ run_story: story }} />}
              {!loadingStory && selected && !story && <p className="text-sm text-muted-foreground">Run details could not be loaded.</p>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DecisionDetail({ item, onChanged, onClose }) {
  const [rule, setRule] = useState("");
  const [ruleScope, setRuleScope] = useState("skill");
  const [rulePreview, setRulePreview] = useState(null);
  const [savingRule, setSavingRule] = useState(false);
  const [team, setTeam] = useState(item?.assigned_team || "");
  const [deadline, setDeadline] = useState(item?.deadline_at ? String(item.deadline_at).slice(0, 16) : "");
  const extractionReview = item?.run_story?.approvals?.find(
    (approval) => approval.review_context?.kind === "document_extraction"
  )?.review_context || item?.run_story?.extraction_reviews?.find((review) => review.review_context?.kind === "document_extraction")?.review_context;

  useEffect(() => {
    setTeam(item?.assigned_team || "");
    setDeadline(item?.deadline_at ? String(item.deadline_at).slice(0, 16) : "");
    setRule("");
    setRulePreview(null);
  }, [item?.id, item?.assigned_team, item?.deadline_at]);

  if (!item) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-border bg-card/50 px-8 py-16 text-center">
        <div>
          <ShieldCheck className="mx-auto text-muted-foreground" size={28} />
          <p className="mt-3 text-sm font-medium text-foreground">Choose a decision</p>
          <p className="mt-1 text-xs text-muted-foreground">You’ll see why it needs you and what is already verified.</p>
        </div>
      </div>
    );
  }

  const snooze = async () => {
    const until = new Date(Date.now() + 60 * 60 * 1000).toISOString();
    await updateAttentionItem(item.id, { state: "snoozed", snoozed_until: until });
    toast.success("Snoozed for one hour");
    onChanged();
  };

  const previewRule = async () => {
    if (!rule.trim() || !item.run_id) return;
    setSavingRule(true);
    try {
      const response = await previewOperationsRunRule(item.run_id, { text: rule.trim(), scope: ruleScope });
      setRulePreview(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not preview the rule"));
    } finally {
      setSavingRule(false);
    }
  };
  const saveRule = async (rerun) => {
    setSavingRule(true);
    try {
      const response = await applyOperationsRunRule(item.run_id, { text: rule.trim(), scope: ruleScope, kind: rulePreview?.kind, impact_preview: rulePreview || {}, rerun, idempotency_key: `teach:${item.run_id}:${Date.now()}` });
      toast.success(rerun && response.data.rerun?.started ? "Rule saved and linked rerun started" : "Rule saved");
      setRule(""); setRulePreview(null); onChanged();
    } catch (error) { toast.error(getApiErrorMessage(error, "Could not save the rule")); }
    finally { setSavingRule(false); }
  };
  const saveAssignment = async () => {
    try { await assignAttentionItem(item.id, { assigned_team: team.trim() || null, deadline_at: deadline ? new Date(deadline).toISOString() : null }); toast.success("Ownership updated"); onChanged(); }
    catch (error) { toast.error(getApiErrorMessage(error, "Ownership could not be updated")); }
  };

  return (
    <div className="overflow-y-auto rounded-lg border border-border bg-card shadow-sm lg:max-h-[calc(100vh-16rem)]">
      <div className="border-b border-border p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Badge variant="outline" className={severityClasses[item.severity]}>{item.severity}</Badge>
            <span className="text-xs text-muted-foreground">{item.job_function}</span>
            {entityLabel(item.entity_ref) && <span className="truncate text-xs text-muted-foreground">· {entityLabel(item.entity_ref)}</span>}
          </div>
          {onClose && <Button type="button" variant="ghost" size="xs" className="shrink-0" onClick={onClose}>Close</Button>}
        </div>
        <h2 className="mt-2 text-lg font-semibold tracking-tight text-foreground">{friendlyInlineCopy(item.title)}</h2>
        <p className="mt-1 max-w-3xl text-xs leading-5 text-muted-foreground">{friendlyInlineCopy(item.source_type === "execution" ? item.summary || item.reason : item.reason || item.summary)}</p>
        {item.source_type === "bubba_native" && item.metadata?.native_url && <a href={item.metadata.native_url} className="mt-3 inline-flex text-xs font-medium text-primary hover:underline">Open canonical record in Bubba</a>}
        {answerPath(item) && <Link to={answerPath(item)} className="mt-3 inline-flex text-xs font-medium text-primary hover:underline">{rerun(item) ? "Sign in if the site asks, and run it again" : "Answer it next to the live browser"}</Link>}
      </div>

      <div className="space-y-5 p-4">
        <RunStory item={item} />

        {extractionReview && (
          <section>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">What was verified</p>
            <ExtractionReview review={extractionReview} />
          </section>
        )}

        {item.can_act && <details className="rounded-xl border border-border p-4"><summary className="cursor-pointer text-sm font-semibold text-foreground">Owner and deadline</summary><div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"><label className="text-xs text-muted-foreground">Team<Input className="mt-1" value={team} onChange={(event) => setTeam(event.target.value)} placeholder="Operations, Billing…" /></label><label className="text-xs text-muted-foreground">Due by<Input className="mt-1" type="datetime-local" value={deadline} onChange={(event) => setDeadline(event.target.value)} /></label><Button variant="outline" onClick={saveAssignment}>Save</Button></div></details>}

        {item.can_act && item.requires_confirmation && (
          <section className="space-y-3 border-t border-border pt-5">
            <div>
              <p className="text-sm font-semibold text-foreground">Your decision</p>
              <p className="mt-1 text-xs text-muted-foreground">The final action will use the same permissions and audit path as the rest of AW.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {item.source_type === "approval" && <>
                <ConfirmationControl item={item} action="approve" label="Review approval" onComplete={onChanged} />
                <ConfirmationControl item={item} action="reject" label="Reject" onComplete={onChanged} />
              </>}
              {item.source_type === "signal_health" && (
                <ConfirmationControl item={item} action="resume_binding" label="Review and resume" onComplete={onChanged} />
              )}
              <Button variant="ghost" onClick={snooze}>Snooze 1 hour</Button>
            </div>
          </section>
        )}

        {item.can_act && item.run_id && (
          <section className="border-t border-border pt-5">
            <div className="flex items-start gap-3">
              <Sparkle className="mt-0.5 text-primary" size={18} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">Teach ActionWeave</p>
                <p className="mt-1 text-xs text-muted-foreground">Create a durable directive with provenance to this run. A rerun is linked; history is never rewritten.</p>
                <textarea value={rule} onChange={(event) => { setRule(event.target.value); setRulePreview(null); }} rows={3} placeholder="Example: Never send a detention request without a signed POD." className="mt-3 w-full rounded-md border border-input bg-background p-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                <div className="mt-2 flex flex-wrap gap-2"><select value={ruleScope} onChange={(event) => { setRuleScope(event.target.value); setRulePreview(null); }} className="h-9 rounded-md border border-input bg-background px-3 text-xs text-foreground"><option value="skill">This Skill</option><option value="global">All work</option></select><Button variant="outline" onClick={previewRule} disabled={!rule.trim() || savingRule}>{savingRule ? "Checking…" : "Preview impact"}</Button></div>
                {rulePreview && <div className="mt-3 rounded-lg bg-muted/40 p-3"><div className="flex items-center gap-2"><Badge variant="outline">{rulePreview.kind}</Badge><span className="text-xs text-muted-foreground">{rulePreview.job_function}</span></div><p className="mt-2 text-xs leading-5 text-muted-foreground">{rulePreview.explanation}</p><p className="mt-2 text-[11px] text-muted-foreground">{rulePreview.recent_runs?.recent_runs || 0} comparable runs · {rulePreview.recent_runs?.exceptions || 0} exceptions</p><div className="mt-3 flex flex-wrap gap-2"><Button onClick={() => saveRule(false)} disabled={savingRule}>Save rule</Button><Button variant="outline" onClick={() => saveRule(true)} disabled={savingRule}>Save and rerun</Button></div></div>}
              </div>
            </div>
          </section>
        )}
        {!item.can_act && <p className="rounded-lg border border-border bg-muted/25 px-3 py-2 text-xs text-muted-foreground">This record is read-only for your current role.</p>}
      </div>
    </div>
  );
}

export default function Operations() {
  const navigate = useNavigate();
  const pageVisible = usePageVisibility();
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState({ items: [], counts: {}, functions: [] });
  const [outcomes, setOutcomes] = useState({ items: [], total: 0, window_hours: 24 });
  const [showOutcomes, setShowOutcomes] = useState(false);
  const [selectedOutcomeId, setSelectedOutcomeId] = useState(null);
  const [outcomeStory, setOutcomeStory] = useState(null);
  const [outcomeStoryLoading, setOutcomeStoryLoading] = useState(false);
  const [outcomesLoading, setOutcomesLoading] = useState(false);
  const [outcomesError, setOutcomesError] = useState("");
  const [setup, setSetup] = useState({ visible: false, items: [], counts: {} });
  const [health, setHealth] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [functionFilter, setFunctionFilter] = useState("");
  const [queueQuery, setQueueQuery] = useState("");
  const [queueSearch, setQueueSearch] = useState("");
  const [queueOffset, setQueueOffset] = useState(0);
  const [queueError, setQueueError] = useState("");
  const [overviewError, setOverviewError] = useState("");
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [liveStatus, setLiveStatus] = useState("offline");
  const [assistantText, setAssistantText] = useState("");
  const [assistantAnswer, setAssistantAnswer] = useState("");
  const [assistantDocument, setAssistantDocument] = useState(null);
  const [commandPreview, setCommandPreview] = useState(null);
  const [mode, setMode] = useState({ mode: "live" });
  const [activeRuns, setActiveRuns] = useState({ items: [] });
  const [runInspector, setRunInspector] = useState(null);
  const [runStoryLoading, setRunStoryLoading] = useState(false);

  const supportBootstrap = useNativeSupportBootstrap();
  const initialQueue = useRef(null);
  const initialQueueTaken = useRef(false);
  const takeTodayQueue = supportBootstrap?.takeTodayQueue;
  const queueLoader = useMemo(() => latestRequest(), []);
  const overviewLoader = useMemo(() => latestRequest(), []);
  useEffect(() => {
    return () => { queueLoader.cancel(); overviewLoader.cancel(); };
  }, [queueLoader, overviewLoader]);
  useEffect(() => {
    const search = queueQuery.trim();
    if (search === queueSearch) return undefined;
    const timer = setTimeout(() => { setQueueSearch(search); setQueueOffset(0); }, 250);
    return () => clearTimeout(timer);
  }, [queueQuery, queueSearch]);

  const loadQueue = useCallback(({ keepSelection = true } = {}) => {
    const params = { function_id: functionFilter || undefined, search: queueSearch, offset: queueOffset, limit: 25 };
    return queueLoader.run(JSON.stringify(params), (signal) => {
      if (signal.aborted) throw new Error('Decision queue request was cancelled');
      if (!initialQueueTaken.current) {
        initialQueueTaken.current = true;
        initialQueue.current = takeTodayQueue?.();
      }
      const pending = initialQueue.current;
      if (pending && !functionFilter && !queueSearch && queueOffset === 0) {
        return pending.then(({ response, error }) => {
          initialQueue.current = null;
          if (error) throw error;
          return response;
        });
      }
      return getOperationsTodayQueue(params, { signal });
    }, {
      success: ({ data }) => {
        setQueue(data);
        setQueueError("");
        const ids = data.items.map((item) => item.id);
        setSelectedId((current) => keepSelection && ids.includes(current) ? current : null);
        if (!data.items.length && queueOffset > 0 && data.counts.total <= queueOffset) {
          setQueueOffset(Math.max(0, Math.ceil(data.counts.total / 25) - 1) * 25);
        }
      },
      failure: (error) => {
        setQueue({ items: [], counts: {}, functions: [] });
        setSelectedId(null);
        setQueueError(getApiErrorMessage(error, "Decision queue could not be loaded"));
      },
      settled: () => setLoading(false),
    });
  }, [functionFilter, queueSearch, queueOffset, queueLoader, takeTodayQueue]);
  const loadQueueRef = useRef(loadQueue);
  loadQueueRef.current = loadQueue;

  const load = useCallback(({ keepSelection = true } = {}) => {
    if (!pageIsVisible()) return Promise.resolve();
    // The durable queue can render before projection, metrics or active runs finish.
    loadQueueRef.current({ keepSelection });
    return overviewLoader.run(functionFilter, (signal) => getOperationsToday({ function_id: functionFilter || undefined, compact: true }, { signal }), {
      success: (response) => {
      setSummary(response.data.summary);
      setSetup(response.data.setup || { visible: false, items: [], counts: {} });
      setHealth(response.data.health);
      setMode(response.data.mode || { mode: "live" });
      setActiveRuns(response.data.active_runs || { items: [] });
      setOverviewError("");
      // The compact overview returns before source projection. Its completion
      // does not invalidate the queue; operations.changed (or fallback polling)
      // fetches the current page when the background projection finishes.
      },
      failure: (error) => {
        setSummary(null); setActiveRuns({ items: [] });
        setOverviewError(getApiErrorMessage(error, "Overview could not be updated"));
      },
      settled: () => setOverviewLoading(false),
    });
  }, [functionFilter, overviewLoader]);

  useEffect(() => {
    if (!pageVisible) {
      queueLoader.cancel(); overviewLoader.cancel();
      // A background tab must not reuse an old entry request on return.
      initialQueueTaken.current = true;
      initialQueue.current = null;
      takeTodayQueue?.();
      return;
    }
    setOverviewLoading(true); setSummary(null); load({ keepSelection: false });
  }, [load, pageVisible, queueLoader, overviewLoader, takeTodayQueue]);
  useEffect(() => {
    if (!pageVisible) return;
    setLoading(true); setSelectedId(null); loadQueue({ keepSelection: false });
  }, [loadQueue, pageVisible]);

  useEffect(() => {
    if (!showOutcomes || !pageVisible) return undefined;
    const controller = new AbortController();
    setOutcomesLoading(true);
    setOutcomesError("");
    setOutcomes({ items: [], total: 0, window_hours: 24 });
    getOperationsTodayOutcomes({ function_id: functionFilter || undefined }, { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setOutcomes(data);
        setSelectedOutcomeId((current) => data.items.some((item) => item.id === current) ? current : data.items[0]?.id || null);
      })
      .catch((error) => { if (!controller.signal.aborted) setOutcomesError(getApiErrorMessage(error, "Completed outcomes could not be loaded")); })
      .finally(() => { if (!controller.signal.aborted) setOutcomesLoading(false); });
    return () => controller.abort();
  }, [showOutcomes, functionFilter, pageVisible]);

  // Live signals. The payload is only ever "something changed", so we re-read
  // through the permission-scoped API rather than merging pushed rows.
  useEffect(() => {
    if (!pageVisible) return undefined;
    setLiveStatus('offline');
    const stop = openOperationsSocket({
      onStatus: setLiveStatus,
      onEvent: (event) => {
        if (!pageIsVisible()) return;
        if (event.type === "operations.changed") {
          load({ keepSelection: true });
          return;
        }
        if (event.type === "run.progress") {
          const runId = event.payload?.execution_id;
          setSelected((current) => {
            if (current && (current.run_id === runId || current.id === runId)) {
              getAttentionItem(current.id)
                .then((response) => setSelected(response.data))
                .catch(() => {});
            }
            return current;
          });
        }
      },
    });
    return stop;
  }, [load, pageVisible]);

  // Poll as the fallback so a dropped socket degrades to a slower refresh
  // instead of a silently stale screen.
  useEffect(() => {
    if (!pageVisible || liveStatus === "live") return undefined;
    const timer = setInterval(() => load({ keepSelection: true }), 20000);
    return () => clearInterval(timer);
  }, [liveStatus, load, pageVisible]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    setSelected(null);
    getAttentionItem(selectedId)
      .then((response) => { if (!cancelled) setSelected(response.data); })
      .catch((error) => { if (!cancelled) toast.error(getApiErrorMessage(error, "Decision details could not be loaded")); });
    return () => { cancelled = true; };
  }, [selectedId]);

  useEffect(() => {
    if (!showOutcomes || !selectedOutcomeId) {
      setOutcomeStory(null);
      setOutcomeStoryLoading(false);
      return undefined;
    }
    let cancelled = false;
    setOutcomeStoryLoading(true);
    getOperationsRunStory(selectedOutcomeId)
      .then((response) => {
        if (!cancelled) setOutcomeStory(response.data);
      })
      .catch((error) => {
        if (!cancelled) {
          setOutcomeStory(null);
          toast.error(getApiErrorMessage(error, "Outcome details could not be loaded"));
        }
      })
      .finally(() => {
        if (!cancelled) setOutcomeStoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [showOutcomes, selectedOutcomeId]);

  const askAssistant = async (event) => {
    event.preventDefault();
    // With a document attached the message is optional: the document IS the request.
    if (!assistantText.trim() && !assistantDocument) return;

    // A document attached here starts governed work, exactly as the Orchestrator's manual trigger
    // does. It used to post intent:"document" to the assistant, which stored the file and replied
    // "…is attached to this governed conversation" -- no trigger, no execution, nothing processing
    // it. An operator who attaches a rate confirmation on their landing page and gets a toast
    // saying "Document attached" reasonably concludes the work has started.
    //
    // This is not an authorization shortcut. The gateway's rule is that COMMAND intents -- mode
    // changes, retries, assignments, snoozes, resolutions -- must travel through the same
    // slide-to-confirm as the visible controls, because they change governance state. A trigger
    // does not: the run it starts is itself governed, can be policy-blocked, and cannot write to a
    // TMS without whatever approval that Skill requires. Same endpoint the Orchestrator calls, same
    // shape of context, so the two paths cannot drift.
    if (assistantDocument) {
      const document = assistantDocument;
      const requestText = assistantText.trim()
        || `Process the attached document ${document.name || document.file_name || ""}`.trim();
      try {
        const response = await createOrchestratorTriggerAsync({
          trigger_type: "manual",
          source: "operations_today",
          context: {
            request: requestText,
            attachments: [{
              id: document.id,
              name: document.name || document.file_name,
              file_name: document.file_name || document.name,
              mime_type: document.mime_type || "application/octet-stream",
              file_size: document.file_size,
            }],
            attachment_note: "1 file(s) attached; inspect them with the Document Agent before acting",
          },
        });
        setAssistantDocument(null);
        setAssistantText("");
        setAssistantAnswer(
          `Started a governed run on ${document.name || document.file_name}. It appears under Happening now; a TMS write still needs whatever approval the Skill requires.`,
        );
        toast.success("Run started");
        // Surface it immediately rather than waiting for the next poll.
        await load({ keepSelection: true });
        return response;
      } catch (error) {
        toast.error(getApiErrorMessage(error, "The run could not be started"));
        return null;
      }
    }

    try {
      const response = await queryOperationsAssistant({
        message: assistantText.trim(),
        attention_item_id: selectedId,
        // The server already infers query/explain/command from whether a decision is selected and
        // whether the message names one, so asking the operator to classify their own sentence was
        // work the gateway was doing anyway.
        intent: "auto",
        document_id: null,
      });
      setAssistantAnswer(response.data.answer);
      setCommandPreview(response.data.command_preview || null);
      setAssistantText("");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Assistant is unavailable"));
    }
    return null;
  };
  const changeMode = async () => {
    const next = mode.mode === "live" ? "internal_only" : mode.mode === "internal_only" ? "read_only" : "live";
    try { const response = await previewOperationsCommand({ command_kind: "set_mode", parameters: { mode: next }, idempotency_key: `mode:${next}:${Date.now()}` }); setCommandPreview(response.data); }
    catch (error) { toast.error(getApiErrorMessage(error, "Mode change could not be prepared")); }
  };
  const uploadDocument = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try { const response = await uploadOperationsDocument(file); setAssistantDocument(response.data); setAssistantIntent("document"); toast.success("Attached — send to start a run on it"); }
    catch (error) { toast.error(getApiErrorMessage(error, "Document could not be attached")); }
    event.target.value = "";
  };
  const listen = () => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) { toast.info("Voice dictation is not supported in this browser"); return; }
    const recognition = new Recognition(); recognition.lang = "en-US"; recognition.interimResults = false;
    recognition.onresult = (event) => setAssistantText(event.results?.[0]?.[0]?.transcript || ""); recognition.onerror = () => toast.error("Voice dictation stopped"); recognition.start();
  };
  const visibleQueueItems = queue.items;

  const inspectRun = async (run) => {
    // Re-selecting the open run closes it, the same gesture the Skill workspace run list uses.
    if (runInspector?.run?.id === run.id) { setRunInspector(null); return; }
    setRunStoryLoading(true);
    try { const response = await getOperationsRunStory(run.id); setRunInspector({ run, story: response.data }); }
    catch (error) { toast.error(getApiErrorMessage(error, "Run details could not be loaded")); }
    finally { setRunStoryLoading(false); }
  };

  return (
    <div className="w-full space-y-2 pb-2">
      <header className="flex min-h-9 flex-col gap-1 border-b border-border/80 pb-1.5 pt-10 md:flex-row md:items-center md:justify-between md:pt-0">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary"><Waveform size={13} /> Operations</div>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h1 className="text-xl font-semibold leading-none tracking-[-0.035em] text-foreground">Today</h1>
          {/* The sentence that used to live here restated what the counts and the empty state already
              say -- three renderings of "nothing needs you" on one screen. The state it carried is
              the only part that was not duplicated, so it survives as a badge. */}
          {/* Only the caught-up state earns a badge here. When there is work, the toolbar chip
              already says how much, and saying "5 need you" twice on one row is the duplication
              this pass set out to remove. */}
          {!loading && !queueError && !queueSearch && !functionFilter && !queue.counts.total && (
            <Badge variant="outline" className="h-5 border-success-border bg-success-surface px-1.5 text-[10px] text-success-foreground">
              Caught up
            </Badge>
          )}
          </div>
        </div>
        <div className="flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5 shadow-sm">
          <span
            className="flex items-center gap-1.5 px-1.5 text-[11px] font-medium text-muted-foreground"
            title={liveStatus === "live"
              ? "Updating live from ActionWeave"
              : "Live connection unavailable; refreshing every 20 seconds"}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${liveStatus === "live" ? "bg-success" : "bg-muted-foreground/50"}`} />
            {liveStatus === "live" ? "Live" : "Polling"}
          </span>
          <Button variant="ghost" size="xs" onClick={() => load()}><ArrowClockwise size={11} />Refresh</Button>
        </div>
      </header>

      <CommandControl preview={commandPreview} onCancel={() => setCommandPreview(null)} onComplete={() => { setCommandPreview(null); load(); }} />

      {/* One toolbar where there were two bands. The three counts were already short enough to read
          as labels; as full cards they were mostly padding, and they pushed the running work and the
          composer below the fold. Same pattern already shipped on Orchestrator and the Skill
          workspace, so this removes a layout from the product rather than adding one. */}
      <div className="flex min-h-8 flex-wrap items-center justify-between gap-x-3 gap-y-1.5 rounded-md border border-border bg-card px-2 py-1">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <TodayChip
            value={loading ? "…" : queueError ? "—" : queue.counts.total || 0}
            label="need you"
            detail={queue.counts.critical ? `${queue.counts.critical} critical` : "Prioritized decisions"}
            // A count that means someone has to act should not read like a statistic.
            tone={queue.counts.total ? "attention" : "neutral"}
          />
          <TodayChip
            value={overviewLoading ? "…" : summary?.runs?.completed ?? "—"}
            label="completed · 24h"
            detail="Outcomes completed in the last 24 hours"
            onClick={summary?.runs?.completed ? () => setShowOutcomes((current) => !current) : undefined}
            actionLabel={summary?.runs?.completed ? `View ${summary.runs.completed}` : null}
            expanded={showOutcomes}
          />
          <TodayChip
            value={summary?.runs?.autonomy_rate == null ? "—" : `${summary.runs.autonomy_rate}%`}
            label="autonomous"
            detail={
              summary?.runs?.autonomy_denominator
                ? `Of ${summary.runs.autonomy_denominator} finished runs · last 24h`
                : "No runs finished in the last 24h"
            }
          />
          {setup.visible && Number(setup.counts?.active || 0) > 0 && (
            <TodayChip
              value={Number(setup.counts.active)}
              label={`setup gap${Number(setup.counts.active) === 1 ? "" : "s"}`}
              detail="Automation coverage needs attention"
              tone="attention"
              onClick={() => navigate("/manage/coverage")}
            />
          )}
        </div>
        {overviewLoading ? <p role="status" className="text-xs text-muted-foreground">Updating overview…</p> : overviewError ? <p role="alert" className="text-xs text-destructive">{overviewError}</p> : <OperationalStatusBar
          mode={mode}
          health={health}
          setup={setup}
          onChangeMode={changeMode}
          onReviewSetup={() => navigate("/manage/coverage")}
        />}
      </div>

      {showOutcomes && (outcomesLoading ? <p role="status" className="p-3 text-xs text-muted-foreground">Loading completed outcomes…</p> : outcomesError ? <p role="alert" className="p-3 text-xs text-destructive">{outcomesError}</p> : (
        <OutcomesPanel
          outcomes={outcomes}
          selectedId={selectedOutcomeId}
          onSelect={setSelectedOutcomeId}
          story={outcomeStory}
          loadingStory={outcomeStoryLoading}
          onClose={() => setShowOutcomes(false)}
        />
      ))}

      {queue.items.length || loading || queueError || queueQuery || functionFilter ? (
      /* Same shell as the Skill workspace queue. Today is an operator surface, and until now it had
         three different ways of opening a detail on one page: this grid with a fixed drawer, the
         outcomes panel, and the Happening now pane. One shell, one close control, one selected-row
         treatment. Below lg it stacks rather than trapping the reader in an overlay. */
      <MasterDetailPane
        data-testid="operations-decision-pane-shell"
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={selected ? selected.title : "Decision"}
        masterWidth="minmax(380px,0.85fr)"
        detailWidth="minmax(560px,1.15fr)"
        detail={selected ? (
          <DecisionDetail
            item={selected}
            onClose={() => setSelectedId(null)}
            onChanged={() => load({ keepSelection: false })}
          />
        ) : (
          <div className="space-y-3 p-4" aria-label="Loading decision details">
            <Skeleton className="h-5 w-2/5" />
            <Skeleton className="h-8 w-4/5" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}
        master={
          <section className="min-w-0">
            <div className="flex min-h-8 flex-wrap items-center gap-2 border-b border-border px-2.5 py-1">
              <h2 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Decision queue</h2>
              <span className="text-[11px] text-muted-foreground">{loading ? "Loading…" : `${queue.items.length ? queueOffset + 1 : 0}–${queueOffset + queue.items.length} of ${queue.counts.total || 0}`}</span>
              <div className="ml-auto flex items-center gap-1.5">
                <input
                  value={queueQuery}
                  maxLength={200}
                  onChange={(event) => setQueueQuery(event.target.value)}
                  placeholder="Search decisions…"
                  aria-label="Search the decision queue"
                  className="h-7 w-[150px] min-w-0 rounded-md border border-input bg-background px-2 text-[11px] text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                />
                {queue.functions?.length > 1 && (
                  <select
                    value={functionFilter}
                    onChange={(event) => { setFunctionFilter(event.target.value); setQueueOffset(0); }}
                    className="h-7 max-w-[150px] rounded-md border border-input bg-background px-2 text-[11px] text-foreground"
                    aria-label="Filter by job function"
                  >
                    <option value="">All my functions</option>
                    {queue.functions.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                )}
              </div>
            </div>
            <div data-testid="operations-attention-list" aria-busy={loading} className="space-y-px p-1">
              {queueError && <p role="alert" className="p-3 text-xs text-destructive">{queueError}</p>}
              {loading ? <div aria-label="Loading decisions" className="space-y-2 p-2"><Skeleton className="h-9" /><Skeleton className="h-9" /><Skeleton className="h-9" /></div> : visibleQueueItems.map((item) => (
                <AttentionRow
                  key={item.id}
                  item={item}
                  active={item.id === selectedId}
                  showQuickActions={!selectedId}
                  onSelect={() => setSelectedId((current) => current === item.id ? null : item.id)}
                  onChanged={() => load({ keepSelection: false })}
                />
              ))}
              {!loading && !queueError && !visibleQueueItems.length && (
                <p className="px-2 py-6 text-center text-[11px] text-muted-foreground">No decisions match this search.</p>
              )}
            </div>
            {(queueOffset > 0 || queue.has_more) && <div className="flex items-center justify-end gap-2 border-t border-border p-2">
              <Button variant="outline" size="sm" disabled={loading || queueOffset === 0} onClick={() => setQueueOffset((value) => Math.max(0, value - 25))}>Previous</Button>
              <Button variant="outline" size="sm" disabled={loading || !queue.has_more} onClick={() => setQueueOffset((value) => value + 25)}>Next</Button>
            </div>}
          </section>
        }
      />
      ) : (
        <Card className="border-dashed shadow-none">
          {/* One line, not a full-height card. This is the state you want to be in -- announcing it
              with a 24px icon and two lines of prose gave the best possible news the most space on
              the page, and pushed the running work that is worth looking at below the fold. */}
          <CardContent className="flex items-center gap-2 px-3 py-2">
            <CheckCircle className="shrink-0 text-success" size={14} />
            <p className="text-xs text-muted-foreground">
              Nothing needs your judgment right now
              {activeRuns.items?.length ? " — running work is below." : "."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Happening now opens its run on the right, the same shape Orchestrator, Roles & Skills and
          the Skill workspace use. It used to render the story in a card stacked underneath, which
          pushed the composer off-screen and meant losing sight of the list to read one run. */}
      <MasterDetailPane
        data-testid="happening-now-pane"
        open={Boolean(runInspector)}
        onClose={() => setRunInspector(null)}
        title={runInspector ? runInspector.run.title : null}
        masterWidth="minmax(320px,0.8fr)"
        detailWidth="minmax(420px,1.2fr)"
        detail={
          <div className="p-3">
            {runStoryLoading && <p className="text-[11px] text-muted-foreground">Loading run story…</p>}
            {!runStoryLoading && runInspector && (
              <>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Active run</p>
                <div className="mt-2"><RunStory item={{ run_story: runInspector.story }} /></div>
              </>
            )}
          </div>
        }
        master={
          <section className="min-w-0 overflow-hidden rounded-md border border-border bg-card">
            <div className="flex min-h-8 items-center gap-2 border-b border-border px-2.5 py-1">
              <Robot size={14} className="text-primary" />
              <h2 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Happening now</h2>
              <span className="text-[9px] text-muted-foreground">{overviewLoading ? "Updating…" : `${activeRuns.items?.length || 0} active`}</span>
            </div>
            <div className="divide-y divide-border/70">
              {(activeRuns.items || []).map((run) => (
                <button
                  type="button"
                  key={run.id}
                  onClick={() => inspectRun(run)}
                  className={cn(
                    "w-full px-2.5 py-2 text-left transition-colors",
                    runInspector?.run?.id === run.id
                      ? "bg-primary/[0.14] shadow-[inset_3px_0_0_0_hsl(var(--primary))] hover:bg-primary/20"
                      : "hover:bg-accent/40",
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-foreground">{run.title}</p>
                      <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{run.job_function} · {run.waiting_reason}</p>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-[11px]">{run.status.replaceAll("_", " ")}</Badge>
                  </div>
                  <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-background">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${run.progress_percent || 5}%` }} />
                  </div>
                </button>
              ))}
              {!overviewLoading && !overviewError && !activeRuns.items?.length && (
                <p className="px-2.5 py-3 text-[11px] text-muted-foreground">No operations are running right now.</p>
              )}
            </div>
          </section>
        }
      />

      <div className="sticky bottom-0 z-20 -mx-2 bg-gradient-to-t from-background via-background/95 to-transparent px-2 pb-1 pt-2">
      {/* mr-24 at every width, not sm:mr-0. FloatingMic is `fixed bottom-6 right-6 z-50` and needs
          81px of clearance (57px button + 24px inset); the old rule reserved 56px on mobile and then
          gave it all back above sm, so on any desktop width the mic sat exactly on top of the send
          button -- measured 32px of overlap on a 32px-wide button, i.e. fully covered and
          unclickable. Found by hit-testing each control's own centre with elementFromPoint. */}
      <Card className="mr-24 border-border bg-card/95 shadow-lg backdrop-blur-xl">
        {/* Column, with the composer row inside it. This was `sm:flex sm:items-center` with the
            answer paragraph as a sibling of the form, so above sm the answer became a flex item in
            the same row and sat on top of the mic and send buttons. It only looked fine while
            answers were short. */}
        <CardContent className="flex flex-col p-2">
          <div className="sm:flex sm:items-center">
          {/* The query/explain/command selector is gone: the gateway resolves intent itself from whether a
              decision is selected and what the message says, so the buttons asked the operator to
              classify a sentence the server was already classifying. Document stays -- it is a file
              picker, not an intent. */}
          <div className="mb-1 flex flex-nowrap gap-0.5 overflow-x-auto sm:mb-0 sm:mr-2"><label className={`inline-flex h-8 shrink-0 cursor-pointer items-center rounded-md px-2.5 text-[11px] font-medium ${assistantDocument ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}><Paperclip size={13} className="mr-1" />Document<input type="file" className="sr-only" onChange={uploadDocument} /></label>{assistantDocument && <Button type="button" size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => setAssistantDocument(null)}>{assistantDocument.name} ×</Button>}</div>
          <form onSubmit={askAssistant} className="flex min-w-0 flex-1 gap-1.5">
            <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-input bg-background px-3">
              <Sparkle size={16} className="shrink-0 text-primary" />
              <input
                value={assistantText}
                onChange={(event) => setAssistantText(event.target.value)}
                className="h-8 min-w-0 flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
                placeholder={selected ? "Ask why this needs you…" : "Ask ActionWeave about today…"}
                aria-label="Ask ActionWeave"
              />
            </div>
            <Button type="button" variant="outline" size="icon" className="hidden h-8 w-8 sm:inline-flex" aria-label="Dictate operations question" onClick={listen}><Microphone size={15} /></Button>
            <Button type="submit" size="icon" className="h-8 w-8" aria-label="Send question"><PaperPlaneTilt size={15} /></Button>
          </form>
          </div>
          {assistantAnswer && <p className="mt-2 rounded-md bg-muted/40 px-3 py-2 text-xs leading-5 text-foreground">{assistantAnswer}</p>}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
